// src/render/audio/unlock.ts — iOS Web Audio unlock helper (CONTEXT D-18).
//
// iOS Safari (and every iOS WebView, since all iOS browsers are forced to
// WebKit) refuses to start an AudioContext outside a user-gesture frame.
// Howler ships an internal autoUnlock dance, but it is not deterministic
// across the password gate + cookie-cached returning-session paths the
// project uses. unlock() is the explicit user-gesture-driven helper:
// called from the password gate's `touchend` (06-03 wires this), it forces
// `Howler.ctx.resume()` then plays a 1-frame silent buffer to cement the
// running state.
//
// Idempotent: a module-level `unlocked` flag short-circuits subsequent
// calls so re-firing the gate handler (e.g. password re-submit, cookie
// re-prompt) costs zero work after the first success.
//
// P6-Pitfall G: probe `Howler.ctx` for null before touching `state` —
// Howler creates the AudioContext lazily, and the cookie-cached returning-
// session path can call unlock() before any Howl construction has run.
// In that case we silently return; the next-frame audio-manager init
// triggers the unlock from inside its own user-gesture callback.

import { Howler } from 'howler';

let unlocked = false;

export function unlock(): void {
  if (unlocked) return;
  if (!Howler.ctx) return;

  if (Howler.ctx.state !== 'running') {
    void Howler.ctx.resume();
  }

  // 1-frame silent buffer — the canonical iOS Web Audio unlock dance per
  // RESEARCH Example 2. Sample rate 22050 keeps the buffer tiny; 1-frame
  // length (1 sample) means the buffer ends instantly so there is no
  // audible artefact.
  const buffer = Howler.ctx.createBuffer(1, 1, 22050);
  const src = Howler.ctx.createBufferSource();
  src.buffer = buffer;
  src.connect(Howler.ctx.destination);
  src.start(0);

  unlocked = true;
}

export function isUnlocked(): boolean {
  return Boolean(Howler.ctx) && Howler.ctx.state === 'running';
}

/**
 * Test-only: reset the module-level `unlocked` flag so unlock() can be
 * driven through its first-call path again. Production callers MUST NOT
 * use this — the flag is intentionally process-lifetime sticky.
 */
export function _resetUnlockedForTests(): void {
  unlocked = false;
}
